import { api } from "../../api/api";
import { IQueryParams, ISearchQueryParams } from "./common.types";

export const getSearchQuery = (queryParams: IQueryParams): string => {
  const { page, rowsPerPage, status, value, columns, fromDate, toDate } =
    queryParams;
  let query = "?";
  if (status !== "-1") {
    query += `status=${status}&`;
  }
  if (columns.length > 0 && value.length > 0) {
    query += `columns=${columns}&`;
  }
  if (columns.length > 0 && value.length > 0) {
    query += `value=${value}&`;
  }
  if (fromDate) {
    query += `from_date=${fromDate}&`;
  }
  if (toDate) {
    query += `to_date=${toDate}&`;
  }
  query += `pageNo=${page}&itemPerPage=${rowsPerPage}`;
  return query;
};


export type IFileExtension = "IMAGE" | "PDF" | "EXCEL" | "OTHER" | "CSV" | "VIDEO";

export const getFileExtensionType = (fileName?: string | null): IFileExtension => {
  if (!fileName || typeof fileName !== "string") {
    return "OTHER";
  }
  const extension = fileName?.split(".").pop()?.toLocaleLowerCase();
  if (
    extension === "jpg" ||
    extension === "jpeg" ||
    extension === "png" ||
    extension === "webp" ||
    extension === "jfif" ||
    extension === "tif"
  )
    return "IMAGE";
  if (extension === "pdf") return "PDF";
  if (extension === "xls" || extension === "xlsx") return "EXCEL";
  return "OTHER";
};

export const getBase64Extension = (extension: string): string => {
  const fileType = getFileExtensionType(extension);
  if (fileType === "IMAGE") return `data:image/${extension};base64`;
  if (fileType === "EXCEL")
    return `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64`;
  return `data:application/${extension};base64`;
};

const arrayBufferToBase64V2 = (buffer: any, extension: string): string => {
  return `${getBase64Extension(extension)},${btoa(
    new Uint8Array(buffer).reduce(function (data, byte) {
      return data + String.fromCharCode(byte);
    }, ""),
  )}`;
};

export const downloadSingleFileAsync = async (fileUrl: string = "") =>
  new Promise<{ fileName: string; data: string; type: IFileExtension }>(
    (resolve, reject) => {
      const payload = {
        type: "json",
        keys: [fileUrl],
      };
      api
        .post("/general/download-files", payload)
        .then((res) => {
          const fileName = fileUrl?.split("/").pop() || "";
          const fileExtension = fileName.split(".")[1] || "";
          const base64Data = arrayBufferToBase64V2(
            res.data[0]?.data,
            fileExtension,
          );
          resolve({
            fileName: fileName,
            data: base64Data,
            type: getFileExtensionType(fileUrl),
          });
        })
        .catch((err) => {
          reject(err);
        });
    },
  );


  export const downloadSingleImageAsync = async (fileUrl: string, abortSignal?: AbortSignal) => {
    const payload = {
      type: "json",
      keys: [fileUrl],
    };
    
    try {
      const res = await api.post("/general/download-files", payload, { signal: abortSignal });
      const fileName = fileUrl?.split("/")[fileUrl?.split("/").length - 1];
      const fileExtension = fileName.split(".")[1];
      const base64Data = arrayBufferToBase64V2(res.data[0]?.data, fileExtension);
      
      return {
        fileName: fileName,
        data: base64Data,
        type: getFileExtensionType(fileUrl) as IFileExtension,
      };
    } catch (err:any) {
      if (err.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        throw err;
      }
    }
  };


  export const getSearchQueryV2 = (queryParams: ISearchQueryParams): string => {
    const { page, rowsPerPage, status, value, columns, date, limit, companyOutletUuid, partnerId, customFilters } =
      queryParams;
    let query = "";
    if (status !== "-1") {
      query += `status=${status}&`;
    }
    if (columns && columns.length > 0 && value) {
      query += `columns=${columns}&value=${value}&`;
    }
    if (date?.fromDate) {
      query += `from_date=${date?.fromDate}&`;
    }
  
  
  
    if (date?.toDate) {
      query += `to_date=${date?.toDate}&`;
    }
  
  
    if(companyOutletUuid) {
      query += `company_outlet_uuid=${companyOutletUuid}&`;
    }
    if (queryParams.advancedFilters && queryParams.advancedFilters.length > 0) {
      query += `advanceFilter=${JSON.stringify(queryParams.advancedFilters)}&`;
    }
    if(queryParams.partnerId) {
      query += `partner_id=${queryParams.partnerId}&`;
    }
    // Add custom filters as query parameters
    if (customFilters) {
      Object.keys(customFilters).forEach((key) => {
        const filterValue = customFilters[key];
        if (filterValue !== null && filterValue !== undefined && filterValue !== "") {
          query += `${key}=${encodeURIComponent(filterValue)}&`;
        }
      });
    }
    query += `pageNo=${page}&itemPerPage=${rowsPerPage}`;
    return query;
    };

/**
 * Adds a search filter to the advanceFilter array if the value is provided
 * @param advanceFilter - The array to add the filter to
 * @param column - The column name(s) to search in
 * @param value - The search value
 * @param logicalOperator - The logical operator (AND or OR), defaults to "AND"
 * @param operator - The comparison operator, defaults to "CONTAINS"
 * @returns The updated advanceFilter array
 */
export const addSearchFilterToAdvanceFilter = (
  advanceFilter: any[],
  column: string[],
  value: string | number | undefined | null,
  logicalOperator: "AND" | "OR" = "AND",
  operator: string = "EQUAL"
): any[] => {
  const stringValue = value !== undefined && value !== null ? String(value) : "";
  if (stringValue && stringValue.trim() !== "") {
    advanceFilter.push({
      column,
      value: stringValue.trim(),
      logicalOperator,
      operator,
    });
  }
  return advanceFilter;
};

/**
 * Reusable reducer helper: upsert an item in a list by id key.
 * If an item with the same id exists, it is replaced; otherwise the item is appended.
 * Use this for local-only updates (e.g. dialog/right panel edits without API) so you
 * don't duplicate find/replace logic in every reducer.
 *
 * @param list - Current list (will not be mutated)
 * @param item - Item to upsert (must have the id key)
 * @param idKey - Key used to match items (e.g. 'crossroads_lead_uuid', 'id')
 * @returns New array with the item updated or pushed
 */
export function upsertItemInListById<T extends Record<string, unknown>>(
  list: T[],
  item: T,
  idKey: keyof T
): T[] {
  const listSafe = Array.isArray(list) ? list : [];
  const id = item[idKey];
  const index = listSafe.findIndex((el) => el[idKey] === id);
  if (index > -1) {
    const next = [...listSafe];
    next[index] = item;
    return next;
  }
  return [...listSafe, item];
}

/**
 * Helper to extract an error message string from an API error response or Error object.
 * @param err - The error object caught (typically AxiosError or generic Error)
 * @param defaultMessage - Optional fallback message if no specific message is found
 * @returns The extracted error message string
 */
export const getApiErrorMessage = (
  err: any,
  defaultMessage: string = "An error occurred"
): string => {
  if (typeof err === "string") {
    return err;
  }
  return (
    err?.response?.data?.message ||
    err?.response?.data?.msg ||
    err?.message ||
    defaultMessage
  );
};

